import clsx from 'clsx';
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { fmtMonthDay, type MemberDTO, type ProgramDTO, type ProgramTaskDTO, type ProjectStatus } from '@wd/shared';
import { errorText } from '../../lib/api';
import { useMembers, useProgramActions, useProjects } from '../../lib/queries';
import { Icon, MoreIcon, StatusIcon } from '../../ui/icons';
import { MenuList, Popover } from '../../ui/menu';
import { Avatar, Button, EmptyState, Modal, Segmented } from '../../ui/primitives';
import { Editable } from './Editable';

// 关键任务：Team 里的项目。计划时间是加入时记下的基线，预计完成就是项目的截止日，两者之差就是偏差。

const STATUS: { value: ProjectStatus; label: string }[] = [
  { value: 'todo', label: '未开始' },
  { value: 'progress', label: '进行中' },
  { value: 'blocked', label: '卡住' },
  { value: 'done', label: '已完成' },
  { value: 'cancelled', label: '已取消' },
];
const STATUS_LABEL = Object.fromEntries(STATUS.map((s) => [s.value, s.label])) as Record<ProjectStatus, string>;

export const md = (d: string | null) => (d ? fmtMonthDay(d) : '?');

/** 按分组排：有分组的按第一次出现的顺序，没分组的放最后 */
export function grouped<T extends { section: string | null }>(tasks: T[]): { section: string | null; tasks: T[] }[] {
  const out: { section: string | null; tasks: T[] }[] = [];
  for (const t of tasks) {
    const g = out.find((x) => x.section === t.section);
    if (g) g.tasks.push(t);
    else out.push({ section: t.section, tasks: [t] });
  }
  return out.sort((a, b) => (a.section === null ? 1 : b.section === null ? -1 : 0));
}

export function SlipTag({ t }: { t: ProgramTaskDTO }) {
  if (t.slipDays == null) return null;
  if (t.slipDays > 0) return <span className="pg-slip late">延后 {t.slipDays} 天</span>;
  if (t.slipDays < 0) return <span className="pg-slip early">提前 {-t.slipDays} 天</span>;
  return <span className="pg-slip">按期</span>;
}

export function TasksTab({ program, mobile }: { program: ProgramDTO; mobile: boolean }) {
  const [adding, setAdding] = useState(false);
  const actions = useProgramActions(program.id);
  if (!program.tasks.length)
    return (
      <>
        <EmptyState
          icon="grid"
          title="还没有关键任务"
          desc="关键任务就是 Team 里的项目：可以把已有的项目加进来，也可以直接新建。加入时记下计划时间，之后截止日的变化都对照它算偏差。"
        >
          {program.canEdit && (
            <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>
              添加关键任务
            </Button>
          )}
        </EmptyState>
        {adding && <AddTasksModal program={program} onClose={() => setAdding(false)} />}
      </>
    );

  return (
    <div className="pg-tasks">
      <div className={clsx('pg-trow head', mobile && 'mobile')}>
        <span>关键任务</span>
        <span>负责人</span>
        <span>计划时间</span>
        <span>预计 / 完成</span>
        <span>状态</span>
        <span>最近进展</span>
        <span />
      </div>
      {grouped(program.tasks).map((g) => (
        <section key={g.section ?? '-'} className="pg-group">
          <div className="pg-group-head">
            {g.section && program.canEdit ? (
              <Editable value={g.section} canEdit onSave={(to) => to && actions.renameSection.mutate({ from: g.section!, to })} maxLength={20} />
            ) : (
              <span>{g.section ?? '未分组'}</span>
            )}
            <span className="c4">{g.tasks.length}</span>
          </div>
          {g.tasks.map((t, i) => (
            <TaskRow key={t.projectId} program={program} t={t} first={i === 0} last={i === g.tasks.length - 1} mobile={mobile} />
          ))}
        </section>
      ))}
      {program.canEdit && (
        <div className="pg-tasks-foot">
          <Button icon="plus" onClick={() => setAdding(true)}>
            添加关键任务
          </Button>
          <span className="t-meta">计划时间是基线，改截止日不会改基线；确实要重新排计划时再改基线</span>
        </div>
      )}
      {adding && <AddTasksModal program={program} onClose={() => setAdding(false)} />}
    </div>
  );
}

type Pop = null | 'menu' | 'status' | 'baseline' | 'due' | 'section';

function TaskRow({ program, t, first, last, mobile }: { program: ProgramDTO; t: ProgramTaskDTO; first: boolean; last: boolean; mobile: boolean }) {
  const actions = useProgramActions(program.id);
  const [pop, setPop] = useState<Pop>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const statusRef = useRef<HTMLButtonElement>(null);
  const close = () => setPop(null);
  const done = t.status === 'done';

  return (
    <div className={clsx('pg-trow', mobile && 'mobile', t.status === 'cancelled' && 'dim')}>
      <span className="task">
        <Link to={`/projects/${t.projectId}`} className="ellipsis">
          <span className="c4">{t.code}</span> {t.name}
        </Link>
      </span>
      <span className="owner">
        <Avatar initial={t.owner.initial} seed={t.owner.name} size={16} />
        {t.owner.name}
      </span>
      <span className="plan">
        {t.baselineStart || t.baselineEnd ? `${md(t.baselineStart)} – ${md(t.baselineEnd)}` : <span className="c4">未设基线</span>}
      </span>
      <span className="due">
        {done ? `已完成 ${md(t.doneAt)}` : t.dueDate ? md(t.dueDate) : <span className="c4">未定</span>}
        <SlipTag t={t} />
      </span>
      <span className="status">
        <button ref={statusRef} type="button" className="pg-status" disabled={!program.canEdit} onClick={() => setPop('status')}>
          <StatusIcon status={t.status} />
          {STATUS_LABEL[t.status]}
        </button>
        <Popover anchor={statusRef} open={pop === 'status'} onClose={close} width={160}>
          <MenuList
            items={STATUS.map((s) => ({ value: s.value, label: s.label, icon: <StatusIcon status={s.value} /> }))}
            selected={t.status}
            onPick={(v) => {
              close();
              if (v !== t.status) actions.updateProject.mutate({ projectId: t.projectId, status: v });
            }}
          />
        </Popover>
      </span>
      <span className="latest" title={t.latest ? `${t.latest.memberName} · ${t.latest.date}\n${t.latest.text}` : undefined}>
        {!t.visible ? (
          <span className="c4">没有权限查看进展</span>
        ) : t.latest ? (
          <>
            <span className="c4">{md(t.latest.date)}</span> {t.latest.text}
          </>
        ) : (
          <span className="c4">还没有日报</span>
        )}
      </span>
      <span className="more">
        {program.canEdit && (
          <>
            <button ref={moreRef} type="button" className="wd-btn icon" aria-label="更多" onClick={() => setPop('menu')}>
              <MoreIcon />
            </button>
            <Popover anchor={moreRef} open={pop === 'menu'} onClose={close} width={200} align="end">
              <MenuList
                items={[
                  { value: 'due', label: '调整截止日', icon: <Icon name="cal" size={14} /> },
                  { value: 'baseline', label: '调整计划时间（基线）', icon: <Icon name="clock" size={14} /> },
                  { value: 'section', label: '改分组', icon: <Icon name="list" size={14} /> },
                  ...(!first ? [{ value: 'up', label: '上移', icon: <Icon name="chevU" size={14} /> }] : []),
                  ...(!last ? [{ value: 'down', label: '下移', icon: <Icon name="chevD" size={14} /> }] : []),
                  { value: 'remove', label: '移出项目集', icon: <Icon name="minus" size={14} /> },
                ]}
                selected={null}
                onPick={(v) => {
                  if (v === 'up' || v === 'down') {
                    close();
                    actions.updateTask.mutate({ projectId: t.projectId, move: v });
                  } else if (v === 'remove') {
                    close();
                    actions.removeTask.mutate(t.projectId);
                  } else setPop(v as Pop);
                }}
              />
            </Popover>
            <Popover anchor={moreRef} open={pop === 'baseline'} onClose={close} width={280} align="end">
              <BaselineForm t={t} onDone={close} programId={program.id} />
            </Popover>
            <Popover anchor={moreRef} open={pop === 'due'} onClose={close} width={280} align="end">
              <DueForm t={t} onDone={close} programId={program.id} />
            </Popover>
            <Popover anchor={moreRef} open={pop === 'section'} onClose={close} width={240} align="end">
              <SectionForm t={t} sections={program.sections} onDone={close} programId={program.id} />
            </Popover>
          </>
        )}
      </span>
    </div>
  );
}

function BaselineForm({ t, programId, onDone }: { t: ProgramTaskDTO; programId: string; onDone: () => void }) {
  const { updateTask } = useProgramActions(programId);
  const [s, setS] = useState(t.baselineStart ?? '');
  const [e, setE] = useState(t.baselineEnd ?? '');
  const [err, setErr] = useState<string | null>(null);
  return (
    <form
      className="wd-pop-form"
      onSubmit={async (ev) => {
        ev.preventDefault();
        try {
          await updateTask.mutateAsync({ projectId: t.projectId, baselineStart: s || null, baselineEnd: e || null });
          onDone();
        } catch (x) {
          setErr(errorText(x));
        }
      }}
    >
      <span className="t-meta">计划时间（基线）：重新排计划时才改，改动会记录</span>
      <div className="row" style={{ gap: 8 }}>
        <input className="wd-input" type="date" value={s} onChange={(x) => setS(x.target.value)} aria-label="计划开始" />
        <input className="wd-input" type="date" value={e} onChange={(x) => setE(x.target.value)} aria-label="计划完成" />
      </div>
      {err && <span className="t-meta c-red">{err}</span>}
      <div className="actions">
        <span className="flex1" />
        <Button variant="text" onClick={onDone}>
          取消
        </Button>
        <Button variant="primary" type="submit" disabled={updateTask.isPending}>
          保存
        </Button>
      </div>
    </form>
  );
}

function DueForm({ t, programId, onDone }: { t: ProgramTaskDTO; programId: string; onDone: () => void }) {
  const { updateProject } = useProgramActions(programId);
  const [d, setD] = useState(t.dueDate ?? '');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const later = !!t.dueDate && !!d && d > t.dueDate;
  return (
    <form
      className="wd-pop-form"
      onSubmit={async (ev) => {
        ev.preventDefault();
        if (!d) return setErr('选一个日期');
        if (later && !reason.trim()) return setErr('往后推需要写一句理由');
        try {
          await updateProject.mutateAsync({ projectId: t.projectId, dueDate: d, dueReason: reason.trim() || null });
          onDone();
        } catch (x) {
          setErr(errorText(x));
        }
      }}
    >
      <input className="wd-input" type="date" autoFocus value={d} onChange={(x) => setD(x.target.value)} aria-label="截止日" />
      {later && <input className="wd-input" value={reason} placeholder="延期理由，会写进进度汇报的延期说明" onChange={(x) => setReason(x.target.value)} />}
      {err && <span className="t-meta c-red">{err}</span>}
      <div className="actions">
        <span className="flex1" />
        <Button variant="text" onClick={onDone}>
          取消
        </Button>
        <Button variant="primary" type="submit" disabled={updateProject.isPending || d === t.dueDate}>
          保存
        </Button>
      </div>
    </form>
  );
}

function SectionForm({ t, sections, programId, onDone }: { t: ProgramTaskDTO; sections: string[]; programId: string; onDone: () => void }) {
  const { updateTask } = useProgramActions(programId);
  const [v, setV] = useState(t.section ?? '');
  return (
    <form
      className="wd-pop-form"
      onSubmit={async (ev) => {
        ev.preventDefault();
        await updateTask.mutateAsync({ projectId: t.projectId, section: v.trim() || null });
        onDone();
      }}
    >
      <input className="wd-input" autoFocus list={`pg-sections-${t.projectId}`} value={v} maxLength={20} placeholder="例如：25kW、裂相机；留空为未分组" onChange={(x) => setV(x.target.value)} />
      <datalist id={`pg-sections-${t.projectId}`}>
        {sections.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
      <div className="actions">
        <span className="flex1" />
        <Button variant="primary" type="submit" disabled={updateTask.isPending}>
          保存
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// 添加关键任务：选已有项目，或新建
// ---------------------------------------------------------------------------

const eligible = (m: MemberDTO) => m.status === 'active' || m.status === 'invited';

function AddTasksModal({ program, onClose }: { program: ProgramDTO; onClose: () => void }) {
  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const { data: projects = [] } = useProjects();
  const { data: members = [] } = useMembers();
  const actions = useProgramActions(program.id);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [section, setSection] = useState('');
  const [name, setName] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [start, setStart] = useState('');
  const [due, setDue] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const inProgram = useMemo(() => new Set(program.tasks.map((t) => t.projectId)), [program.tasks]);
  const list = projects
    .filter((p) => p.status !== 'cancelled' && !inProgram.has(p.id))
    .filter((p) => !q.trim() || `${p.code} ${p.name} ${p.ownerName}`.toLowerCase().includes(q.trim().toLowerCase()));

  const busy = actions.addTasks.isPending || actions.createTask.isPending;
  const save = async () => {
    setErr(null);
    try {
      if (mode === 'existing') await actions.addTasks.mutateAsync([...picked].map((projectId) => ({ projectId, section: section.trim() || null })));
      else
        await actions.createTask.mutateAsync({ name, ownerId, participantIds: [], section: section.trim() || null, startDate: start || null, dueDate: due || null });
      onClose();
    } catch (e) {
      setErr(errorText(e));
    }
  };

  return (
    <Modal
      width={600}
      title="添加关键任务"
      subtitle={program.name}
      onClose={onClose}
      footNote={mode === 'existing' ? '计划时间先按项目现在的开始日和截止日记下，之后可以调整' : '会在 Team 里新建一个项目，负责人的日报会按它预填'}
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            取消
          </Button>
          <Button variant="primary" disabled={busy || (mode === 'existing' ? !picked.size : !name.trim() || !ownerId)} onClick={() => void save()}>
            {mode === 'existing' ? (picked.size ? `加入 ${picked.size} 个` : '加入') : '新建并加入'}
          </Button>
        </>
      }
    >
      <div className="pf-form">
        <Segmented
          items={[
            { value: 'existing', label: '选已有项目' },
            { value: 'new', label: '新建项目' },
          ]}
          value={mode}
          onChange={setMode}
        />
        <label>
          <span className="k">分组（可选）</span>
          <input className="wd-input" list="pg-add-sections" maxLength={20} value={section} placeholder="例如：25kW、25kW 降本、裂相机" onChange={(e) => setSection(e.target.value)} />
          <datalist id="pg-add-sections">
            {program.sections.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </label>
        {mode === 'existing' ? (
          <div className="pf-field">
            <input className="wd-input" value={q} placeholder="搜索编号、名称或负责人" onChange={(e) => setQ(e.target.value)} />
            <div className="pg-pick">
              {list.slice(0, 200).map((p) => {
                const on = picked.has(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={clsx('pg-pick-row', on && 'on')}
                    onClick={() =>
                      setPicked((s) => {
                        const n = new Set(s);
                        if (n.has(p.id)) n.delete(p.id);
                        else n.add(p.id);
                        return n;
                      })
                    }
                  >
                    <span className={clsx('wd-check', on && 'on')}>{on && <Icon name="check" size={12} color="var(--on-accent)" stroke={2} />}</span>
                    <span className="c4">{p.code}</span>
                    <span className="flex1 ellipsis">{p.name}</span>
                    <span className="c4">{p.ownerName}</span>
                    <span className="c4">{p.dueDate ? md(p.dueDate) : ''}</span>
                  </button>
                );
              })}
              {!list.length && <div className="t-meta" style={{ padding: 12 }}>没有可以加入的项目</div>}
            </div>
          </div>
        ) : (
          <>
            <div className="pf-row">
              <label>
                <span className="k">名称</span>
                <input className="wd-input" autoFocus maxLength={40} value={name} placeholder="例如：MATIC-SW-AFCI 开发" onChange={(e) => setName(e.target.value)} />
              </label>
              <label>
                <span className="k">负责人</span>
                <select className="pf-select" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
                  <option value="">选择</option>
                  {members.filter(eligible).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                      {m.teamName ? ` · ${m.teamName}` : ''}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="pf-row">
              <label>
                <span className="k">计划开始</span>
                <input className="wd-input" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
              </label>
              <label>
                <span className="k">计划完成（同时作为截止日）</span>
                <input className="wd-input" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
              </label>
            </div>
          </>
        )}
        {err && <div className="t-meta c-red">{err}</div>}
      </div>
    </Modal>
  );
}
